/**
 * Shared substrate for wall edits: junction graph, wall geometry
 * primitives, and the cascade that turns a *displacement field* into a
 * plan.
 *
 * Every wall edit in the floor plan is the same thing at bottom — some
 * junctions of the structure move, everything attached to them follows. An op
 * supplies only the field: stretch (`wallStretch.js`) supplies a half-plane
 * step, where every junction inside the translate-rectangle moves by
 * `delta * dir` and the rest stay.
 *
 * `cascadeDisplacement` consumes the field plus the op's already-computed
 * `affectedStructureWalls` and produces the rest of the plan (room walls,
 * exterior walls, children, polygon corners, structure-floor corners, room
 * classification, free-standing objects). Stretch is its only caller today;
 * the engine stays general because the freehand gestures come back through it
 * — see `reference-docs/wall-drag/overview.md`.
 *
 * No Firestore, no React.
 */

import { ALIGNMENT_ANGLE_TOLERANCE } from '@shared-lib/geometry/wallGeometry';
import { apply3DRotationTo2D } from './geometryUtils';
import { buildWallStoryLookup } from '@shared-lib/geometry/storyMembership';

// Floating-point safety margin only — junction classification is strict.
export const PARALLEL_TOL = 1e-9;
// 5 cm — propagated walls below this are flagged as collapsing.
export const MIN_WALL_LENGTH = 0.05;
// 4° axis-alignment tolerance, as a dot-product threshold.
export const PARALLEL_DOT = Math.cos(ALIGNMENT_ANGLE_TOLERANCE);
// 15 cm — same tolerance wall-merging uses for junction-endpoint coincidence.
export const ENDPOINT_TOL = 0.15;
// 30 cm — fallback for the "connected enough to propagate" check
// (`classifyWallEndpoints`). Endpoints often sit ~5–15 cm away from the
// wall they should T-junction against, due to thickness offsets in scan
// data; without this fallback, a free endpoint would trip the
// independent-edit path and the user's stretch wouldn't propagate.
export const T_JUNCTION_TOL = 0.3;

export const CHILD_ARRAYS = ['doors', 'windows', 'openings', 'objects'];

export const ZERO_DISP = { x: 0, z: 0 };

export const isZeroDisp = (d) =>
  !d || (Math.abs(d.x) < 1e-9 && Math.abs(d.z) < 1e-9);

export const sameDisp = (a, b) =>
  Math.abs((a?.x || 0) - (b?.x || 0)) < 1e-9 &&
  Math.abs((a?.z || 0) - (b?.z || 0)) < 1e-9;

export const getXZ = (corner) => {
  if (corner == null) return null;
  if (Array.isArray(corner)) {
    return { x: corner[0], z: corner[1] };
  }
  return {
    x: corner.x !== undefined ? corner.x : 0,
    z: corner.z !== undefined ? corner.z : 0,
  };
};

export function wallEndpointsXZ(wall) {
  const dir = apply3DRotationTo2D(wall.rotation);
  const halfL = (wall.dimensions?.x || 0) / 2;
  return {
    a: {
      x: wall.center.x - dir[0] * halfL,
      z: wall.center.z - dir[1] * halfL,
    },
    b: {
      x: wall.center.x + dir[0] * halfL,
      z: wall.center.z + dir[1] * halfL,
    },
    dir: { x: dir[0], z: dir[1] },
    length: wall.dimensions?.x || 0,
  };
}

export function pointsEqualXZ(p, q, tol = ENDPOINT_TOL) {
  return Math.abs(p.x - q.x) < tol && Math.abs(p.z - q.z) < tol;
}

/** Distance from point `p` to the closest point on segment (a, b). */
export function pointSegmentDistanceXZ(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < 1e-9) return Math.hypot(p.x - a.x, p.z - a.z);
  let t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

/**
 * Each room's floor polygon as a closed ring of XZ points. Rings shorter than
 * a triangle are dropped — they carry no direction to read.
 */
export function collectRoomPolygons(structureData) {
  const polygons = [];
  for (const room of structureData?.rooms || []) {
    const ring = [];
    for (const corner of room.floors?.[0]?.polygonCorners || []) {
      const xz = getXZ(corner);
      if (xz) ring.push(xz);
    }
    if (ring.length >= 3) polygons.push(ring);
  }
  return polygons;
}

/**
 * Does `polygon` BOUND the wall at `point`, or does it merely CAP it there?
 *
 * This is the difference between a wall end that bounds a room and one that
 * dangles into it, and the room's floor polygon is the only place it is
 * written down — the two look identical in the wall data.
 *
 * The end of an interior partition has ONE signature, whether the partition is
 * a single wall or a chain of them: the boundary comes out along the wall,
 * crosses a short END CAP no wider than the wall itself, and goes back the
 * other way. Two runs along the wall's own axis, opposite in direction, joined
 * by a cap:
 *
 *   ... ───────────●         CAPPED — the end of an interior partition group.
 *            ┌─────┤         Both runs lie along the wall's axis and point
 *   ... ─────┘     ●         opposite ways; the cap between them is ≤ the
 *                            wall's thickness.
 *
 *   ... ───────────●         BOUNDED — the boundary reaches the end and
 *                  │         leaves in some other direction, or simply runs
 *                  │         straight through it. Either way the room is
 *                            bounded here, so the wall's end is anchored.
 *
 * Reading each run against the WALL's axis rather than against each other is
 * what makes it hold up on scan data: two faces of one partition can sit a few
 * degrees out of parallel with each other and still each be plainly along the
 * wall. And a boundary that passes straight through the point with no vertex
 * near it bounds the end too — scans leave 0.3-0.6 m gaps mid-run where two
 * walls never met, and there is no corner there to read.
 */
export function polygonBoundsWallEnd(polygon, wall, point) {
  const n = polygon?.length || 0;
  if (n < 3 || !point || !wall?.center || !wall?.dimensions) return false;
  const { a, b } = wallEndpointsXZ(wall);
  const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
  const ux = (b.x - a.x) / len;
  const uz = (b.z - a.z) / len;
  const halfThickness = (wall.dimensions.z || 0) / 2;
  const at = (i) => polygon[((i % n) + n) % n];

  // 1. Is this ring anywhere near the end? Its edges run along the wall's
  //    FACES, half a thickness off the centerline, so that is the reach.
  const reach = halfThickness + ENDPOINT_TOL;
  let near = false;
  for (let i = 0; i < n && !near; i += 1) {
    if (pointSegmentDistanceXZ(point, at(i), at(i + 1)) <= reach) near = true;
  }
  if (!near) return false;

  // 2. Capped? Find each run of vertices sitting on the end and read the edges
  //    that bracket it.
  const onEnd = (i) => pointsEqualXZ(at(i), point, reach);
  const alongWall = (p, q) => {
    const ex = q.x - p.x;
    const ez = q.z - p.z;
    const elen = Math.hypot(ex, ez);
    if (elen < 1e-9) return 0;
    const dot = (ex * ux + ez * uz) / elen;
    return Math.abs(dot) >= PARALLEL_DOT ? Math.sign(dot) : 0;
  };
  for (let i = 0; i < n; i += 1) {
    // Only start a run at its first member, so each run is visited once.
    if (!onEnd(i) || onEnd(i - 1)) continue;
    let last = i;
    let length = 1;
    while (length < n && onEnd(last + 1)) {
      last += 1;
      length += 1;
    }
    if (length >= n) return false; // the whole ring sits on the point
    const arriving = alongWall(at(i - 1), at(i));
    const leaving = alongWall(at(last), at(last + 1));
    const capSpan = Math.hypot(at(last).x - at(i).x, at(last).z - at(i).z);
    if (
      arriving !== 0 &&
      arriving === -leaving &&
      capSpan <= 2 * halfThickness + ENDPOINT_TOL
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Classify a wall's two endpoints as connected / free. Three tiers: tight
 * (another wall's endpoint within ENDPOINT_TOL), loose (within
 * T_JUNCTION_TOL of another wall's body), and bounding — a room polygon that
 * bounds rather than caps this end (`polygonBoundsWallEnd`), which anchors it
 * however far the nearest wall is. `polygons` comes from
 * `collectRoomPolygons`; omit it and only the two wall tiers apply.
 */
export function classifyWallEndpoints(wall, allWalls, polygons = []) {
  const { a, b } = wallEndpointsXZ(wall);
  let aConnected = false;
  let bConnected = false;
  for (const polygon of polygons) {
    if (!aConnected && polygonBoundsWallEnd(polygon, wall, a)) {
      aConnected = true;
    }
    if (!bConnected && polygonBoundsWallEnd(polygon, wall, b)) {
      bConnected = true;
    }
    if (aConnected && bConnected) return { a, b, aConnected, bConnected };
  }
  for (const other of allWalls || []) {
    if (!other || other.id === wall.id || !other.center || !other.dimensions) {
      continue;
    }
    const oe = wallEndpointsXZ(other);
    if (pointsEqualXZ(a, oe.a) || pointsEqualXZ(a, oe.b)) aConnected = true;
    if (pointsEqualXZ(b, oe.a) || pointsEqualXZ(b, oe.b)) bConnected = true;
    // Loose: endpoint near the other wall's body (offset T-junction).
    if (!aConnected && pointSegmentDistanceXZ(a, oe.a, oe.b) < T_JUNCTION_TOL) {
      aConnected = true;
    }
    if (!bConnected && pointSegmentDistanceXZ(b, oe.a, oe.b) < T_JUNCTION_TOL) {
      bConnected = true;
    }
    if (aConnected && bConnected) break;
  }
  return { a, b, aConnected, bConnected };
}

/**
 * Shift each polygonCorner by the per-end offset between the wall's old
 * endpoints (oldA, oldB) and its new endpoints (newA, newB). Each corner
 * anchors to whichever old endpoint it sits closer to along the wall axis
 * and shifts by that endpoint's delta — so miter insets and other absolute
 * offsets from the wall ends are preserved.
 *
 * The (oldA → newA, oldB → newB) correspondence is established by distance:
 * callers don't have to pass the new endpoints in any particular A/B order.
 */
export function shiftPolygonCornersByEndpoints(
  polygonCorners,
  oldA,
  oldB,
  newA,
  newB
) {
  if (!Array.isArray(polygonCorners)) return polygonCorners;
  const dAA = Math.hypot(newA.x - oldA.x, newA.z - oldA.z);
  const dAB = Math.hypot(newA.x - oldB.x, newA.z - oldB.z);
  const [oldForA, oldForB] = dAA <= dAB ? [oldA, oldB] : [oldB, oldA];
  const offsetForA = { x: newA.x - oldForA.x, z: newA.z - oldForA.z };
  const offsetForB = { x: newB.x - oldForB.x, z: newB.z - oldForB.z };
  const dirX = oldB.x - oldA.x;
  const dirZ = oldB.z - oldA.z;
  const len = Math.hypot(dirX, dirZ);
  const ux = len > 0 ? dirX / len : 1;
  const uz = len > 0 ? dirZ / len : 0;
  const mid = (oldA.x * ux + oldA.z * uz + oldB.x * ux + oldB.z * uz) / 2;
  const offsetForPoint = (p) => {
    const proj = p.x * ux + p.z * uz;
    const anchoredToOldA = proj < mid;
    if (oldForA === oldA) {
      return anchoredToOldA ? offsetForA : offsetForB;
    }
    return anchoredToOldA ? offsetForB : offsetForA;
  };
  return polygonCorners.map((corner) => {
    const xz = getXZ(corner) || { x: 0, z: 0 };
    const o = offsetForPoint(xz);
    if (Array.isArray(corner)) return [corner[0] + o.x, corner[1] + o.z];
    return { ...corner, x: (corner.x || 0) + o.x, z: (corner.z || 0) + o.z };
  });
}

/**
 * Rebuild a wall from new endpoints: center / length / rotation.y, plus a
 * per-end shift of `polygonCorners` so the 3D-box outline tracks the new
 * centerline. `Door.jsx`'s `correctedCenter` snaps onto the wall plane via
 * `polygonCorners[0]`, so stale corners leave doors on the pre-edit plane.
 */
export function wallFromEndpoints(oldWall, newA, newB) {
  const dx = newB.x - newA.x;
  const dz = newB.z - newA.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-9) {
    return {
      ...oldWall,
      dimensions: { ...oldWall.dimensions, x: length },
    };
  }
  const dir = { x: dx / length, z: dz / length };
  // Spread first so we never *create* a `y: undefined` key when the input
  // wall was stored without `center.y` — that tripped Firestore's
  // "Unsupported field value: undefined" on write-back.
  const newCenter = {
    ...oldWall.center,
    x: (newA.x + newB.x) / 2,
    z: (newA.z + newB.z) / 2,
  };
  // Match rewrapRoomWall's convention: rotationY = atan2(-dirZ, dirX).
  const newRotationY = Math.atan2(-dir.z, dir.x);
  const result = {
    ...oldWall,
    center: newCenter,
    dimensions: { ...oldWall.dimensions, x: length },
    rotation: { ...oldWall.rotation, y: newRotationY },
  };
  if (Array.isArray(oldWall.polygonCorners)) {
    const oldEnds = wallEndpointsXZ(oldWall);
    result.polygonCorners = shiftPolygonCornersByEndpoints(
      oldWall.polygonCorners,
      oldEnds.a,
      oldEnds.b,
      newA,
      newB
    );
  }
  return result;
}

/**
 * Re-derive a room wall (or exterior wall) under a structure-wall change by
 * applying *per-endpoint offsets* rather than proportional mapping, so
 * absolute miter insets are preserved.
 *
 * `pickOffset(point, { offsetA, offsetB, oldEnds })` decides which of the
 * structure wall's two endpoint offsets a given point inherits. Stretch cuts
 * by the boundary line; the drag cuts by the nearer structure-wall endpoint.
 */
export function snapRoomWallToStructure(
  roomWall,
  oldStructureWall,
  newStructureWall,
  pickOffset
) {
  if (!roomWall) return roomWall;
  const oldEnds = wallEndpointsXZ(oldStructureWall);
  const newEnds = wallEndpointsXZ(newStructureWall);
  const offsetA = {
    x: newEnds.a.x - oldEnds.a.x,
    z: newEnds.a.z - oldEnds.a.z,
  };
  const offsetB = {
    x: newEnds.b.x - oldEnds.b.x,
    z: newEnds.b.z - oldEnds.b.z,
  };
  const ctx = { offsetA, offsetB, oldEnds, newEnds };
  const offsetForPoint = (p) => pickOffset(p, ctx);
  const shifted = (p) => {
    const o = offsetForPoint(p);
    return { ...p, x: p.x + o.x, z: p.z + o.z };
  };

  const roomEnds = wallEndpointsXZ(roomWall);
  const newWall = wallFromEndpoints(
    roomWall,
    shifted(roomEnds.a),
    shifted(roomEnds.b)
  );

  // Same per-end offset for the alignment corners.
  if (roomWall.alignment) {
    const newAlignment = { ...roomWall.alignment };
    for (const k of ['bottomLeft', 'bottomRight', 'bottomMiddle']) {
      const pt = newAlignment[k];
      if (!pt) continue;
      const o = offsetForPoint(pt);
      newAlignment[k] = { ...pt, x: pt.x + o.x, z: pt.z + o.z };
    }
    newWall.alignment = newAlignment;
  }

  return newWall;
}

/**
 * Pick the offset of whichever structure-wall endpoint the point sits
 * nearer to along the wall axis. The drag's cut — there is no boundary
 * line to project against.
 */
export const nearestEndpointOffset = (p, { offsetA, offsetB, oldEnds }) => {
  const ux = oldEnds.dir.x;
  const uz = oldEnds.dir.z;
  const mid =
    (oldEnds.a.x * ux +
      oldEnds.a.z * uz +
      oldEnds.b.x * ux +
      oldEnds.b.z * uz) /
    2;
  return p.x * ux + p.z * uz < mid ? offsetA : offsetB;
};

export function* iterateAffectedRoomChildren(room, roomWall) {
  for (const arrayName of CHILD_ARRAYS) {
    const arr = room[arrayName];
    if (!arr) continue;
    for (const child of arr) {
      if (!child || child.parentWallIds?.[0] !== roomWall.id) continue;
      yield { arrayName, child };
    }
  }
}

export function* iterateAffectedStructureChildren(structureData, wallId) {
  for (const arrayName of CHILD_ARRAYS) {
    const arr = structureData[arrayName];
    if (!arr) continue;
    for (const child of arr) {
      if (!child || child.parentWallIds?.[0] !== wallId) continue;
      yield { arrayName, child };
    }
  }
}

/**
 * Cluster every connection point in the structure — wall endpoints AND room
 * polygon corners — into junctions, so two walls meeting at a corner can
 * never disagree about whether they follow an edit. Any two points within
 * ENDPOINT_TOL join the same junction.
 *
 * Returns `{ junctions, indexFor(point) }`. `junctions[i].members` is not
 * kept — callers key displacement by index.
 */
export function buildJunctionGraph(structureData) {
  // A junction is every point that touches another within ENDPOINT_TOL,
  // through any chain of such neighbours. It used to be the points near a
  // junction's FIRST point, so two points 5.7 cm apart could land in
  // different junctions when only one was near that first point — and
  // classify apart, one moving and one not: on Lake Francois (D7155424) the
  // living room's outline corner stayed while the wall end beside it moved
  // 14 cm, which sloped the outline and left a strip of floor past the wall.
  //
  // `pos` is the junction's first point, kept as its representative. Every
  // point is kept in `members` so callers that need an extremum over the
  // junction (stretch projects each member onto its axis and takes the max)
  // don't have to re-derive it.
  //
  // Only points that share a storey touch. Stacked storeys share X/Z, so a
  // chain left free to cross floors ran from an upstairs bathroom's corners
  // through the ground floor's to points 30 cm away (Curlew, 7657745E) and
  // moved an upstairs wall the edit never reached. A wall belongs to its own
  // storey and to that of every room built on it (`buildWallStoryLookup`, the
  // rule the storey scope uses), so a shared stairwell wall still joins both;
  // anything unstamped is on every storey.
  const lookup = buildWallStoryLookup(
    structureData?.rooms || [],
    structureData?.walls || []
  );
  const points = []; // { x, z, stories: Set | null (every storey) }
  for (const wall of structureData?.walls || []) {
    if (!wall || !wall.center || !wall.dimensions) continue;
    const ends = wallEndpointsXZ(wall);
    const stories = lookup.get(wall.id) || null;
    points.push(
      { x: ends.a.x, z: ends.a.z, stories },
      { x: ends.b.x, z: ends.b.z, stories }
    );
  }
  for (const room of structureData?.rooms || []) {
    const stories = Number.isFinite(room?.story) ? new Set([room.story]) : null;
    for (const corner of room.floors?.[0]?.polygonCorners || []) {
      const xz = getXZ(corner);
      if (xz) points.push({ x: xz.x, z: xz.z, stories });
    }
  }

  const near = (p, q) =>
    Math.abs(p.x - q.x) < ENDPOINT_TOL && Math.abs(p.z - q.z) < ENDPOINT_TOL;
  const shareStorey = (p, q) => {
    if (!p.stories || !q.stories) return true;
    for (const s of p.stories) if (q.stories.has(s)) return true;
    return false;
  };

  // Union-find over the points: every touching pair joins one set.
  const parent = points.map((_, i) => i);
  const root = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (!near(points[i], points[j]) || !shareStorey(points[i], points[j]))
        continue;
      const ri = root(i);
      const rj = root(j);
      if (ri !== rj) parent[Math.max(ri, rj)] = Math.min(ri, rj);
    }
  }

  // One junction per set, in order of each set's first point. `stories` is
  // every storey its points are on, null when one of them is on every
  // storey (unstamped).
  const junctions = []; // { pos: {x,z}, members: [{x,z}], stories }
  const junctionOfRoot = new Map();
  const junctionOfPoint = points.map((p, i) => {
    const r = root(i);
    if (!junctionOfRoot.has(r)) {
      junctionOfRoot.set(r, junctions.length);
      junctions.push({
        pos: { x: p.x, z: p.z },
        members: [],
        stories: new Set(),
      });
    }
    const k = junctionOfRoot.get(r);
    const j = junctions[k];
    j.members.push({ x: p.x, z: p.z });
    if (!p.stories) j.stories = null;
    else if (j.stories) for (const st of p.stories) j.stories.add(st);
    return k;
  });

  // The junction a point belongs to: the one holding the nearest point it
  // touches. Every point the graph was built from finds its own junction.
  const indexFor = (point) => {
    if (!point) return -1;
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < points.length; i++) {
      if (!near(points[i], point)) continue;
      const d = Math.hypot(points[i].x - point.x, points[i].z - point.z);
      if (d < bestD) {
        bestD = d;
        best = junctionOfPoint[i];
      }
    }
    return best;
  };

  // `wallStories`: the storeys each structure wall is on, the lookup the
  // junctions were built with (a wall missing from it is on every storey).
  return { junctions, indexFor, wallStories: lookup };
}

const shiftPoint = (p, d) => ({ ...p, x: p.x + d.x, z: p.z + d.z });

/**
 * The point on `wall`'s centreline that `p` sits across from, clamped to the
 * wall's own span. A room wall is drawn half a wall thickness inboard of the
 * structure wall it rides on; this is the host geometry it corresponds to.
 */
export function projectOntoWallSpan(p, wall) {
  const ends = wallEndpointsXZ(wall);
  const len = ends.length || 0;
  if (len <= 1e-9) return { x: ends.a.x, z: ends.a.z };
  const s = Math.min(
    len,
    Math.max(0, (p.x - ends.a.x) * ends.dir.x + (p.z - ends.a.z) * ends.dir.z)
  );
  return { x: ends.a.x + ends.dir.x * s, z: ends.a.z + ends.dir.z * s };
}

/** `child` moved rigidly by `d`, or null when `d` moves it nowhere. */
function movedBy(child, d) {
  if (!d || isZeroDisp(d)) return null;
  return {
    ...child,
    center: {
      ...child.center,
      x: child.center.x + d.x,
      z: child.center.z + d.z,
    },
  };
}

// Below this the wall kept its direction (a stretch along itself or a
// translate) and its children follow the op's own rule.
const REANGLE_EPS_RAD = 1e-9;

/**
 * A child (door, window, opening, wall-mounted object) carried by a wall that
 * an edit RE-ANGLES: the affine image of the wall's line, the one map that
 * keeps a point on the line on the new line, at the same fraction along it —
 * the map room walls on a re-angled host already ride (see `pickOffset` in
 * wallStretch's cascade) — with its offset out from the line, and its
 * rotation, turned by the wall's own turn. A rigid move by
 * where the child sits leaves it off the re-angled wall: 12.6 cm and 8.4° on
 * Lake Francois's bathroom door.
 *
 * Returns null when the wall kept its direction, so the caller falls back to
 * its own rule.
 */
export function rideReangledWall(child, oldWall, newWall) {
  if (!child?.center || !oldWall || !newWall) return null;
  const oldEnds = wallEndpointsXZ(oldWall);
  const newEnds = wallEndpointsXZ(newWall);
  if (!(oldEnds.length > 1e-9) || !(newEnds.length > 1e-9)) return null;
  const turn = Math.atan2(
    oldEnds.dir.x * newEnds.dir.z - oldEnds.dir.z * newEnds.dir.x,
    oldEnds.dir.x * newEnds.dir.x + oldEnds.dir.z * newEnds.dir.z
  );
  if (Math.abs(turn) < REANGLE_EPS_RAD) return null;
  const c = child.center;
  // The child as a point on the wall's line plus an offset out from it: the
  // point rides the line's affine image, and the offset turns with the wall.
  // Moving only the point left anything standing out from the wall — a
  // cabinet's centre 30 cm out — off it once the wall turned.
  const ou = oldEnds.dir;
  const along = (c.x - oldEnds.a.x) * ou.x + (c.z - oldEnds.a.z) * ou.z;
  const out = (c.x - oldEnds.a.x) * -ou.z + (c.z - oldEnds.a.z) * ou.x;
  const t = along / oldEnds.length;
  const offA = { x: newEnds.a.x - oldEnds.a.x, z: newEnds.a.z - oldEnds.a.z };
  const offB = { x: newEnds.b.x - oldEnds.b.x, z: newEnds.b.z - oldEnds.b.z };
  const onLine = {
    x: oldEnds.a.x + ou.x * along + offA.x + (offB.x - offA.x) * t,
    z: oldEnds.a.z + ou.z * along + offA.z + (offB.z - offA.z) * t,
  };
  const nu = newEnds.dir;
  const after = {
    ...child,
    center: { ...c, x: onLine.x - nu.z * out, z: onLine.z + nu.x * out },
  };
  if (child.rotation) {
    // `rotation.y` turns the other way to the (x, z) plane's angle: a wall
    // runs along (cos ry, −sin ry).
    after.rotation = { ...child.rotation, y: (child.rotation.y || 0) - turn };
  }
  return after;
}

/**
 * Turn a displacement field into the derived half of a plan.
 *
 * @param {Object} structureData - structure the op is running over (already
 *   narrowed to the storey scope by the caller).
 * @param {Object} opts
 * @param {Array}  opts.affectedStructureWalls - `[{ wallId, kind, before,
 *   after, reangled? }]`, already computed by the op. `reangled` marks a wall
 *   the op turns (not just stretches along itself): its children ride it.
 * @param {Function} opts.junctionDisp - `(point) => {x,z}` displacement of
 *   the junction a point belongs to. Zero vector = static.
 * @param {Function} opts.childDisp - `(child, entry) => {x,z}|null` how a
 *   child parented to an affected wall moves. `entry` is that wall's
 *   affectedStructureWalls record. `null` = leave it alone.
 * @param {Function} opts.freeObjectDisp - `(obj, roomId) => {x,z}|null` for
 *   objects not parented to an affected wall.
 * @param {Function} opts.pickOffset - per-end offset picker handed to
 *   `snapRoomWallToStructure`.
 * @param {Function} [opts.onStraddleWall] - `(wall, dispA, dispB, meta) =>
 *   void` hook for op-specific slant errors on a wall whose endpoints
 *   disagree. Called for the step-2.4 sweep only.
 * @param {Function} [opts.onStraddleEdge] - `(room, edgeIndex, cornerA,
 *   cornerB) => void` hook for op-specific slant errors on a polygon edge
 *   whose endpoints disagree.
 * @param {boolean} [opts.propagate=true] - run the structure-wide passes
 *   (2.4 room-wall sweep, polygon corners, structure-floor corners, room
 *   classification, free-standing objects). Independent-wall edits pass
 *   false and handle their own corners.
 */
export function cascadeDisplacement(structureData, opts) {
  const {
    affectedStructureWalls,
    junctionDisp,
    childDisp,
    freeObjectDisp,
    pickOffset,
    onStraddleWall,
    onStraddleEdge,
    propagate = true,
    errors = [],
  } = opts;

  const derivedRoomWalls = [];
  const derivedExteriorWalls = [];
  const affectedItems = [];
  const affectedPolygonCorners = [];
  const affectedStructureFloorCorners = [];
  const affectedRoomWallByRoomId = new Map();
  const roomClassification = { translate: new Set(), reshape: new Set() };

  const affectedStructureWallsById = new Map();
  for (const entry of affectedStructureWalls) {
    affectedStructureWallsById.set(entry.wallId, entry);
  }

  const pushChild = (arrayName, child, roomId, entry, scope) => {
    if (!child?.center) return;
    // A child on a wall the op RE-ANGLES (`entry.reangled`, set by the op
    // for a wall outside its parallel tolerance) rides the wall; the op's own
    // rule — a rigid move by where the child sits — is right for a wall that
    // only stretches along itself, which every scanned wall a hair off axis
    // does.
    const after =
      (entry?.reangled && rideReangledWall(child, entry.before, entry.after)) ||
      movedBy(child, childDisp(child, entry));
    if (!after) return;
    const item = {
      kind: arrayName.slice(0, -1),
      id: child.id,
      roomId: roomId || null,
      parentWallIds: child.parentWallIds || [],
      before: child,
      after,
    };
    if (scope) item.scope = scope;
    affectedItems.push(item);
  };

  // 1. Cascade each affected structure wall to its room walls, exterior
  //    walls, and every child parented to any of them.
  for (const entry of affectedStructureWalls) {
    const oldStructureWall = entry.before;
    const newStructureWall = entry.after;
    const wallId = entry.wallId;

    for (const room of structureData.rooms || []) {
      for (const roomWall of room.walls || []) {
        if (!roomWall || roomWall.alignment?.structureWallId !== wallId) {
          continue;
        }

        // Every room wall snaps, the older scans' too. On those the room wall
        // shares its structure wall's id, which read as "the same wall" and
        // used to take the structure wall whole — but it runs on the room's
        // face, half a thickness inboard and shorter by its mitres, so the
        // copy put it on the centreline (all 15 such walls in the corpus).
        // Where the two really do coincide the snap returns the structure
        // wall's geometry anyway: the per-end offsets land on its new ends.
        const newRoomWall = snapRoomWallToStructure(
          roomWall,
          oldStructureWall,
          newStructureWall,
          pickOffset
        );

        derivedRoomWalls.push({
          roomId: room.id,
          wallId: roomWall.id,
          structureWallId: wallId,
          before: roomWall,
          after: newRoomWall,
        });

        if (!affectedRoomWallByRoomId.has(room.id)) {
          affectedRoomWallByRoomId.set(room.id, new Set());
        }
        affectedRoomWallByRoomId.get(room.id).add(roomWall.id);

        for (const { arrayName, child } of iterateAffectedRoomChildren(
          room,
          roomWall
        )) {
          pushChild(arrayName, child, room.id, entry);
        }
      }
    }

    // Structure-level children parented to this structure wall. The same
    // physical opening often appears in several arrays (room.doors +
    // structure.doors + exterior.doors); one rule keeps them in sync.
    for (const { arrayName, child } of iterateAffectedStructureChildren(
      structureData,
      wallId
    )) {
      pushChild(arrayName, child, null, entry);
    }

    for (const extWall of structureData.exterior?.walls || []) {
      if (!extWall || extWall.alignment?.structureWallId !== wallId) continue;
      derivedExteriorWalls.push({
        wallId: extWall.id,
        structureWallId: wallId,
        before: extWall,
        after: snapRoomWallToStructure(
          extWall,
          oldStructureWall,
          newStructureWall,
          pickOffset
        ),
      });
    }

    // Exterior-level children match by `parentIdentifier`, which points
    // straight at the structure wall id.
    for (const arrayName of ['doors', 'windows', 'openings']) {
      const arr = structureData.exterior?.[arrayName];
      if (!Array.isArray(arr)) continue;
      for (const child of arr) {
        if (!child || child.parentIdentifier !== wallId) continue;
        pushChild(arrayName, child, null, entry, 'exterior');
      }
    }
  }

  if (!propagate) {
    return {
      derivedRoomWalls,
      derivedExteriorWalls,
      affectedItems,
      affectedPolygonCorners,
      affectedStructureFloorCorners,
      roomClassification,
      affectedRoomWallByRoomId,
      affectedStructureWallsById,
      errors,
    };
  }

  const structureWallsById = new Map();
  for (const wall of structureData.walls || []) {
    if (wall?.id && wall.center && wall.dimensions)
      structureWallsById.set(wall.id, wall);
  }

  // 2. Room walls on UNAFFECTED structure walls. A long structure wall
  //    whose endpoints both stay put never enters affectedStructureWalls,
  //    but room walls that sit at a moving junction must still move.
  //    Classify each remaining room wall by its OWN endpoints.
  for (const room of structureData.rooms || []) {
    const done = affectedRoomWallByRoomId.get(room.id);
    for (const roomWall of room.walls || []) {
      if (!roomWall || !roomWall.center || !roomWall.dimensions) continue;
      if (done && done.has(roomWall.id)) continue;
      const swId = roomWall.alignment?.structureWallId;
      if (swId && affectedStructureWallsById.has(swId)) continue;
      const ends = wallEndpointsXZ(roomWall);
      // Classify each endpoint where it MEETS its host structure wall, not
      // where the room wall's own face sits — a room wall is inset half a
      // wall thickness, and classifying it on its own can put it on the
      // opposite side of the divider from the host it rides on, which
      // carries it clean off a wall that never moved.
      const host = swId ? structureWallsById.get(swId) : null;
      const dA = junctionDisp(
        host ? projectOntoWallSpan(ends.a, host) : ends.a
      );
      const dB = junctionDisp(
        host ? projectOntoWallSpan(ends.b, host) : ends.b
      );
      if (isZeroDisp(dA) && isZeroDisp(dB)) continue;
      if (!sameDisp(dA, dB) && onStraddleWall) {
        if (onStraddleWall(roomWall, dA, dB, { room }) === 'skip') continue;
      }
      const after = wallFromEndpoints(
        roomWall,
        shiftPoint(ends.a, dA),
        shiftPoint(ends.b, dB)
      );
      if (roomWall.alignment) {
        const na = { ...roomWall.alignment };
        for (const k of ['bottomLeft', 'bottomRight', 'bottomMiddle']) {
          if (!na[k]) continue;
          const d = junctionDisp(na[k]);
          if (isZeroDisp(d)) continue;
          na[k] = { ...na[k], x: na[k].x + d.x, z: na[k].z + d.z };
        }
        after.alignment = na;
      }
      derivedRoomWalls.push({
        roomId: room.id,
        wallId: roomWall.id,
        structureWallId: swId || null,
        before: roomWall,
        after,
      });
      if (!affectedRoomWallByRoomId.has(room.id)) {
        affectedRoomWallByRoomId.set(room.id, new Set());
      }
      affectedRoomWallByRoomId.get(room.id).add(roomWall.id);
    }
  }

  // 3. Room polygon corners — driven off the same junction graph as the
  //    walls, so a corner shared by two walls inherits their verdict.
  for (const room of structureData.rooms || []) {
    const polygonCorners = room.floors?.[0]?.polygonCorners || [];
    if (polygonCorners.length === 0) continue;
    const n = polygonCorners.length;
    const cornerXZ = polygonCorners.map((c) => getXZ(c));
    const cornerDisp = cornerXZ.map((xz) =>
      xz ? junctionDisp(xz) : ZERO_DISP
    );

    if (onStraddleEdge) {
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        if (sameDisp(cornerDisp[i], cornerDisp[j])) continue;
        onStraddleEdge(room, i, cornerXZ[i], cornerXZ[j]);
      }
    }

    let hasStatic = false;
    let firstMoving = null;
    let uniformMoving = true;
    for (let i = 0; i < n; i++) {
      const d = cornerDisp[i];
      if (isZeroDisp(d)) {
        hasStatic = true;
        continue;
      }
      if (firstMoving === null) firstMoving = d;
      else if (!sameDisp(firstMoving, d)) uniformMoving = false;
      affectedPolygonCorners.push({
        roomId: room.id,
        cornerIndex: i,
        before: { x: cornerXZ[i].x, z: cornerXZ[i].z },
        after: { x: cornerXZ[i].x + d.x, z: cornerXZ[i].z + d.z },
      });
    }
    // A room whose corners ALL move by the same vector is rigidly
    // translated — its ceiling rides along with an inline XZ shift. Any
    // mix (some static, or differing displacements) reshapes it, and the
    // caller regenerates its ceiling server-side.
    if (firstMoving !== null) {
      if (!hasStatic && uniformMoving)
        roomClassification.translate.add(room.id);
      else roomClassification.reshape.add(room.id);
    }
  }

  // 4. Structure-floor polygon corners. `structure.floors[]` is its own
  //    scan-derived geometry (the IFC/DXF converters read it directly), not
  //    a derived view of the room floors — cascade it too or exports emit a
  //    stale slab beside the correct per-room floors.
  if (Array.isArray(structureData.floors)) {
    for (let fi = 0; fi < structureData.floors.length; fi++) {
      const floor = structureData.floors[fi];
      const polygonCorners = floor?.polygonCorners || [];
      for (let i = 0; i < polygonCorners.length; i++) {
        const xz = getXZ(polygonCorners[i]);
        if (!xz) continue;
        const d = junctionDisp(xz);
        if (isZeroDisp(d)) continue;
        affectedStructureFloorCorners.push({
          floorIndex: fi,
          cornerIndex: i,
          before: { x: xz.x, z: xz.z },
          after: { x: xz.x + d.x, z: xz.z + d.z },
        });
      }
    }
  }

  // 5. Free-standing objects (no parent, or parent not affected).
  const affectedItemKeys = new Set(
    affectedItems.map((it) => `${it.roomId || ''}:${it.kind}:${it.id}`)
  );
  const handleFreeStanding = (obj, roomId) => {
    if (!obj || !obj.center) return;
    if (affectedItemKeys.has(`${roomId || ''}:object:${obj.id}`)) return;
    const parentId = obj.parentWallIds?.[0];
    if (parentId && affectedStructureWallsById.has(parentId)) return;
    if (
      parentId &&
      roomId &&
      affectedRoomWallByRoomId.get(roomId)?.has(parentId)
    ) {
      return;
    }
    const d = freeObjectDisp ? freeObjectDisp(obj, roomId) : null;
    if (!d || isZeroDisp(d)) return;
    affectedItems.push({
      kind: 'object',
      id: obj.id,
      roomId: roomId || null,
      parentWallIds: obj.parentWallIds || [],
      before: obj,
      after: {
        ...obj,
        center: { ...obj.center, x: obj.center.x + d.x, z: obj.center.z + d.z },
      },
    });
  };
  for (const obj of structureData.objects || []) handleFreeStanding(obj, null);
  for (const room of structureData.rooms || []) {
    for (const obj of room.objects || []) handleFreeStanding(obj, room.id);
  }

  return {
    derivedRoomWalls,
    derivedExteriorWalls,
    affectedItems,
    affectedPolygonCorners,
    affectedStructureFloorCorners,
    roomClassification,
    affectedRoomWallByRoomId,
    affectedStructureWallsById,
    errors,
  };
}

/**
 * Resolve the canonical structure wall (and the room wall the user grabbed,
 * when they grabbed one) from whatever id the gesture published. In bold
 * view the canvas draws structure walls; in room view it draws room walls.
 */
export function resolveDraggedWall(structureData, wallId, { near } = {}) {
  const structureWall = (structureData?.walls || []).find(
    (w) => w && w.id === wallId
  );
  if (structureWall) {
    // A structure wall usually carries SEVERAL room walls — the "long shared
    // wall" spanning bath → bedroom → garage is the norm, not the exception.
    // Taking the first in array order hands back an arbitrary segment, which
    // for a gesture means the edit lands on a wall the user never touched.
    // `near` (the grabbed point) picks the segment that owns it.
    const candidates = [];
    for (const room of structureData.rooms || []) {
      for (const rw of room.walls || []) {
        if (!rw) continue;
        // A room wall whose id equals the structure wall's is the legacy
        // scanned vintage (`legacyPreEditWall`).
        if (rw.id === wallId || rw.alignment?.structureWallId === wallId) {
          candidates.push({ rw, roomId: room.id });
        }
      }
    }
    if (candidates.length === 0) {
      return { structureWall, roomWall: null, roomWallId: null, roomId: null };
    }
    let best = candidates[0];
    if (near && candidates.length > 1) {
      let bestD = Infinity;
      for (const c of candidates) {
        if (!c.rw.center || !(c.rw.dimensions?.x > 0)) continue;
        const { a, b } = wallEndpointsXZ(c.rw);
        const d = Math.min(
          Math.hypot(near.x - a.x, near.z - a.z),
          Math.hypot(near.x - b.x, near.z - b.z)
        );
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
    }
    // The room wall itself rides along: on the older scans it shares the
    // structure wall's id, so looking the id up again finds the structure
    // wall (the centreline) instead of the face the measurement reads.
    return {
      structureWall,
      roomWall: best.rw,
      roomWallId: best.rw.id,
      roomId: best.roomId,
    };
  }
  for (const room of structureData?.rooms || []) {
    for (const rw of room.walls || []) {
      if (!rw || rw.id !== wallId) continue;
      const swId = rw.alignment?.structureWallId;
      const sw = swId
        ? (structureData.walls || []).find((w) => w && w.id === swId)
        : null;
      return {
        structureWall: sw,
        roomWall: rw,
        roomWallId: rw.id,
        roomId: room.id,
      };
    }
  }
  return {
    structureWall: null,
    roomWall: null,
    roomWallId: null,
    roomId: null,
  };
}

/** The drawn wall the gesture is moving — a room wall in room view, a
 * structure wall in bold view. */
export function findDrawnWall(structureData, wallId) {
  const structureWall = (structureData?.walls || []).find(
    (w) => w && w.id === wallId
  );
  if (structureWall) return structureWall;
  for (const room of structureData?.rooms || []) {
    for (const rw of room.walls || []) {
      if (rw && rw.id === wallId) return rw;
    }
  }
  return null;
}
